import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Plus, Search, X, Pencil, Eye, Building2, Users, Stethoscope, BadgeIndianRupee } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { toast } from 'react-toastify';
import Pagination from '../components/Pagination';
import { useNavigate } from 'react-router-dom';
import { selectTenantList, setTenantStatus } from '../redux/tenantSlice';
import { getCountries } from '../redux/masterSlice';
import { useTranslation } from 'react-i18next';
import CommonLoader from '../components/CommonLoader';
import MasterLabel from '../components/common/MasterLabel';
import ErrorPage from '../components/ErrorPage';
import useCanManage from '../hooks/useCanManage';

const MySwal = withReactContent(Swal);

// The plan value is INTERNAL commercial data — this list is super-admin only,
// and the same figure must never appear on a tenant-facing surface.
const inrFormatter = new Intl.NumberFormat('en-IN', {
    style: 'currency', currency: 'INR', maximumFractionDigits: 0,
});

// Super-admin management of tenants (hospitals/clinics on the shared server).
// Mirrors pages/Admin.jsx so the two management screens behave identically.
//
// No delete action by design — a tenant owns doctors, appointments and clinical
// records. Deactivating is the supported way to take one offline, and it blocks
// login for that tenant's whole staff.
export default function TenantManagement() {
    const dispatch = useDispatch();
    const { t } = useTranslation(['tenantpage']);
    const { tenantList, tenantTotal, tenantListLoading, mutating, error } = useSelector((state) => state.tenant);
    const { countries } = useSelector((state) => state.master);

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(5);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
    const [searchError, setSearchError] = useState('');
    const [activeTab, setActiveTab] = useState(() => sessionStorage.getItem('tenantActiveTab') || 'active');
    const [refreshKey, setRefreshKey] = useState(0);

    // Create/edit is a full PAGE now (no modal): /tenants/new and
    // /tenants/:tenantId/edit — the row is passed via route state so the form
    // seeds instantly, with a fetch fallback for refreshes.
    const navigate = useNavigate();
    // Write-side controls hide without the manage key — the API refuses
    // them anyway (hooks/useCanManage.js).
    const canManage = useCanManage('tenants.manage');

    const searchTimeoutRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        sessionStorage.setItem('tenantActiveTab', activeTab);
    }, [activeTab]);

    const validateSearchInput = (value) => {
        const sanitized = value.replace(/[<>]/g, '');
        if (sanitized.length > 50) return t('tenantpage.search_too_long', 'Search term cannot exceed 50 characters');
        if (sanitized.trim().length === 0 && sanitized.length > 0) return t('tenantpage.search_spaces_only', 'Search term cannot contain only spaces');
        return '';
    };

    useEffect(() => {
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        if (!searchError) {
            searchTimeoutRef.current = setTimeout(() => {
                setDebouncedSearchQuery(searchQuery.trim());
                setCurrentPage(1);
            }, 500);
        }
        return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
    }, [searchQuery, searchError]);

    useEffect(() => {
        dispatch(getCountries());
    }, [dispatch]);

    useEffect(() => {
        if (searchError) return;
        dispatch(selectTenantList({
            page: currentPage,
            limit: itemsPerPage,
            search: debouncedSearchQuery,
            status: activeTab,
        }));
    }, [currentPage, itemsPerPage, debouncedSearchQuery, activeTab, dispatch, searchError, refreshKey]);

    const refetch = () => setRefreshKey((k) => k + 1);

    const handleSearch = (e) => {
        const sanitized = e.target.value.replace(/[<>]/g, '');
        const finalValue = sanitized.length > 50 ? sanitized.substring(0, 50) : sanitized;
        setSearchQuery(finalValue);
        setSearchError(validateSearchInput(finalValue));
        setCurrentPage(1);
    };

    const handleStatusToggle = async (tenant) => {
        const isActive = tenant.status === 'active';
        const newStatus = isActive ? 'inactive' : 'active';

        // Deactivating is consequential — it signs out an entire hospital's
        // staff — so spell out the effect and the staff count in the prompt
        // rather than asking a generic "are you sure?".
        const staff = (tenant.doctorCount || 0) + (tenant.adminCount || 0);
        const result = await MySwal.fire({
            title: isActive
                ? t('tenantpage.deactivate_confirm_title', 'Deactivate this tenant?')
                : t('tenantpage.activate_confirm_title', 'Activate this tenant?'),
            text: isActive
                ? t('tenantpage.deactivate_confirm_text', `${staff} user(s) will be blocked from signing in. No data is deleted.`)
                : t('tenantpage.activate_confirm_text', 'Its users will be able to sign in again.'),
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#e5e7eb',
            confirmButtonText: t('tenantpage.confirm', 'Confirm'),
            cancelButtonText: t('tenantpage.cancel', 'Cancel'),
        });
        if (!result.isConfirmed) return;

        try {
            await dispatch(setTenantStatus({ tenantId: tenant.tenantId, status: newStatus })).unwrap();
            refetch();
            toast.success(newStatus === 'active'
                ? t('tenantpage.status_activated', 'Tenant activated')
                : t('tenantpage.status_deactivated', 'Tenant deactivated'));
        } catch (err) {
            toast.warn(typeof err?.error === 'string' ? err.error : t('tenantpage.status_update_error', 'Failed to update tenant status'));
        }
    };

    const handlePageChange = (page, newItemsPerPage) => {
        if (newItemsPerPage && newItemsPerPage !== itemsPerPage) {
            setItemsPerPage(newItemsPerPage);
            setCurrentPage(1);
        } else {
            setCurrentPage(page);
        }
    };

    const indexOfFirst = (currentPage - 1) * itemsPerPage;

    if (error) {
        return <ErrorPage error={error} pageName={t('tenantpage.all_tenants', 'Tenants')} />;
    }

    return (
        <div data-testid="tenant-page-container" className="bg-white p-4 rounded-2xl shadow-md sm:p-6 w-full">
            {/* Header */}
            <div data-testid="tenant-page-header" className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
                <h1 data-testid="tenant-page-title" className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent ml-2">
                    {t('tenantpage.all_tenants', 'Tenants')}
                </h1>
                {canManage && (
                <button
                    data-testid="add-tenant-button"
                    onClick={() => navigate('/tenants/new')}
                    disabled={tenantListLoading}
                    className={`group flex items-center gap-2 w-full sm:w-auto cursor-pointer px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm ${tenantListLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                    <Plus className="w-5 h-5" />
                    <span>{t('tenantpage.add_tenant', 'Add Tenant')}</span>
                </button>
                )}
            </div>

            {/* Tabs + search */}
            <div className="mb-6 flex flex-col sm:flex-row gap-2 sm:items-center">
                <div className="relative flex bg-gradient-to-r from-gray-50 to-blue-50/30 p-1 rounded-2xl shadow border border-gray-200 gap-2">
                    {['active', 'inactive'].map((tab) => (
                        <button
                            key={tab}
                            data-testid={`tenant-tab-${tab}`}
                            onClick={() => { setActiveTab(tab); setCurrentPage(1); }}
                            className={`relative px-6 py-2 text-sm rounded-xl transition-colors duration-300 flex-1 z-10 ${activeTab === tab ? 'text-white' : 'text-gray-600 hover:text-[#4ABDBD] cursor-pointer'}`}
                        >
                            {activeTab === tab && (
                                <motion.div
                                    layoutId="activeTenantStatusTab"
                                    className="absolute inset-0 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] rounded-xl shadow-lg !m-0"
                                    transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                                />
                            )}
                            <span className="relative z-20">
                                {tab === 'active' ? t('tenantpage.active', 'Active') : t('tenantpage.inactive', 'Inactive')}
                            </span>
                        </button>
                    ))}
                </div>

                <div className="relative w-full sm:w-80">
                    <input
                        ref={inputRef}
                        data-testid="tenant-search-input"
                        type="text"
                        placeholder={t('tenantpage.search_placeholder', 'Search name / id / contact…')}
                        value={searchQuery}
                        onChange={handleSearch}
                        maxLength={50}
                        className={`w-full p-2 pl-10 pr-8 border rounded-xl focus:outline-none focus:ring-2 ${searchError ? 'border-red-500 focus:ring-red-500 bg-red-50' : 'border-gray-300 focus:ring-[#4ABDBD]'} ${tenantListLoading ? 'opacity-70' : ''}`}
                    />
                    <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#4ABDBD]" />
                    {searchQuery && (
                        <button
                            data-testid="clear-tenant-search"
                            onClick={() => { setSearchQuery(''); setSearchError(''); }}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500 cursor-pointer"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    )}
                    {searchError && <p className="text-red-500 text-xs mt-1">{searchError}</p>}
                </div>
            </div>

            {tenantListLoading ? (
                <div data-testid="tenant-loading-container" className="flex flex-col items-center justify-center py-4 pt-12">
                    <CommonLoader />
                    <span className="text-xl text-[#4ABDBD]">{t('tenantpage.loading', 'Loading...')}</span>
                </div>
            ) : (
                <>
                    {/* Desktop table */}
                    <div className="hidden sm:block overflow-x-auto rounded-md border border-gray-200">
                        <table data-testid="tenants-table" className="w-full border-collapse rounded-md overflow-hidden">
                            <thead>
                                <tr className="bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white">
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('tenantpage.serial_no', 'S.No')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('tenantpage.name', 'Tenant')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('tenantpage.country', 'Country')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('tenantpage.staff', 'Staff')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('tenantpage.contact', 'Contact')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('tenantpage.plan_value', 'Plan value')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('tenantpage.status', 'Status')}</th>
                                    <th className="p-3 text-center text-xs font-bold uppercase tracking-wider">{t('tenantpage.action', 'Action')}</th>
                                </tr>
                            </thead>
                            <tbody data-testid="tenants-table-body">
                                {(tenantList || []).length > 0 ? (
                                    tenantList.map((tenant, index) => {
                                        const isActive = tenant.status === 'active';
                                        return (
                                            <tr key={tenant.tenantId} data-testid={`tenant-row-${tenant.tenantId}`} className="border-b border-gray-100/50 hover:bg-gradient-to-r hover:from-blue-50/80 hover:to-cyan-50/80 transition-all duration-300 group">
                                                <td className="p-3 text-xs text-gray-600">{indexOfFirst + index + 1}</td>
                                                <td className="p-3 text-xs text-gray-600">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-10 h-10 rounded-full bg-[#4ABDBD] flex items-center justify-center shadow-md flex-shrink-0">
                                                            <Building2 className="w-5 h-5 text-white" />
                                                        </div>
                                                        <div className="flex flex-col">
                                                            <span className="font-semibold text-gray-700">{tenant.name || t('moc.hr_detail_hospital', 'Hospital')}</span>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="p-3 text-xs text-gray-600">
                                                    {tenant.countryId ? (
                                                        <MasterLabel
                                                            type="masterCountry"
                                                            id={tenant.countryId}
                                                            field="countryName"
                                                            fallback={countries?.find((c) => c.countryId === tenant.countryId)?.countryName || '-'}
                                                        />
                                                    ) : '-'}
                                                    {/* The city (owner, 29 Sep 2026) — optional, so an
                                                        older hospital simply shows its country alone. */}
                                                    {tenant.city && (
                                                        <span data-testid={`tenant-city-${tenant.tenantId}`} className="block text-[11px] text-gray-400">{tenant.city}</span>
                                                    )}
                                                </td>
                                                <td className="p-3 text-xs text-gray-600">
                                                    <div className="flex items-center gap-3">
                                                        <span className="inline-flex items-center gap-1" title={t('tenantpage.doctors', 'Doctors')}>
                                                            <Stethoscope className="w-3.5 h-3.5 text-[#4ABDBD]" />{tenant.doctorCount ?? 0}
                                                        </span>
                                                        <span className="inline-flex items-center gap-1" title={t('tenantpage.admins', 'Admins')}>
                                                            <Users className="w-3.5 h-3.5 text-[#4ABDBD]" />{tenant.adminCount ?? 0}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="p-3 text-xs text-gray-600">
                                                    <div className="flex flex-col">
                                                        <span>{tenant.contactEmail || '-'}</span>
                                                        {tenant.contactPhone && <span className="text-[11px] text-gray-400">{tenant.contactPhone}</span>}
                                                    </div>
                                                </td>
                                                <td className="p-3 text-xs" data-testid={`tenant-plan-value-${tenant.tenantId}`}>
                                                    {/* A tenant priced before pricing existed, or never
                                                        priced, shows a dash — not ₹0, which would read
                                                        as "free" rather than "unknown". The MOC
                                                        open-platform tenant is neither: it is the
                                                        platform's own tenant and is never quoted, so it
                                                        says so rather than sharing the dash with the
                                                        customers whose price is merely missing. */}
                                                    {tenant.isOpenPlatform ? (
                                                        <span className="text-gray-400 font-semibold">
                                                            {t('tenantpage.platform_tenant_short', 'Platform')}
                                                        </span>
                                                    ) : tenant.planPricing ? (
                                                        <div className="flex flex-col">
                                                            <span className="font-semibold text-[#2D8A8A]">
                                                                {inrFormatter.format(Number(tenant.planPricing.finalMonthly) || 0)}
                                                            </span>
                                                            <span className="text-[11px] text-gray-400">
                                                                {t('tenantpage.per_month', 'per month')}
                                                            </span>
                                                        </div>
                                                    ) : (
                                                        <span className="text-gray-400">—</span>
                                                    )}
                                                </td>
                                                <td className="p-3 text-xs">
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            type="button"
                                                            role="switch"
                                                            aria-checked={isActive}
                                                            data-testid={`tenant-status-toggle-${tenant.tenantId}`}
                                                            onClick={() => canManage && !mutating && handleStatusToggle(tenant)}
                                                            disabled={mutating || !canManage}
                                                            title={isActive ? t('tenantpage.deactivate', 'Deactivate') : t('tenantpage.activate', 'Activate')}
                                                            className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors duration-300 ${isActive ? 'bg-gradient-to-r from-[#4ABDBD] to-[#2D8A8A]' : 'bg-gray-300'} ${mutating ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                                        >
                                                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-300 ${isActive ? 'translate-x-6' : 'translate-x-1'}`} />
                                                        </button>
                                                        <span className={`text-xs font-semibold ${isActive ? 'text-green-700' : 'text-gray-500'}`}>
                                                            {isActive ? t('tenantpage.active', 'Active') : t('tenantpage.inactive', 'Inactive')}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="p-3 text-xs">
                                                    <div className="flex justify-center gap-2">
                                                        <button
                                                            data-testid={`view-tenant-${tenant.tenantId}`}
                                                            onClick={() => navigate(`/tenants/${tenant.tenantId}/view`)}
                                                            title={t('tenantpage.view', 'View')}
                                                            className="group/btn text-[#4ABDBD] hover:text-white p-2 rounded-xl bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110"
                                                        >
                                                            <Eye className="h-4 w-4" />
                                                        </button>
                                                        {/* The tenant's plan from the platform (doc/5.10.2026.md). */}
                                                        <button
                                                            data-testid={`plan-tenant-${tenant.tenantId}`}
                                                            onClick={() => navigate(`/tenants/${tenant.tenantId}/plan`)}
                                                            title={t('tenantBilling.plan_action', 'Plan')}
                                                            className="group/btn text-[#4ABDBD] hover:text-white p-2 rounded-xl bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110"
                                                        >
                                                            <BadgeIndianRupee className="h-4 w-4" />
                                                        </button>
                                                        {canManage && (
                                                        <button
                                                            data-testid={`edit-tenant-${tenant.tenantId}`}
                                                            onClick={() => navigate(`/tenants/${tenant.tenantId}/edit`, { state: { tenant } })}
                                                            title={t('tenantpage.edit', 'Edit')}
                                                            className="group/btn text-[#4ABDBD] hover:text-white p-2 rounded-xl bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110"
                                                        >
                                                            <Pencil className="h-4 w-4" />
                                                        </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr data-testid="no-tenants-row">
                                        <td colSpan="8" className="p-3 text-center text-gray-500">
                                            {t('tenantpage.no_tenants_found', 'No tenants found.')}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile cards */}
                    <div className="sm:hidden flex flex-col gap-3">
                        {(tenantList || []).length > 0 ? tenantList.map((tenant) => {
                            const isActive = tenant.status === 'active';
                            return (
                                <div key={tenant.tenantId} data-testid={`tenant-card-${tenant.tenantId}`} className="border border-gray-200 rounded-xl p-3 shadow-sm">
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="flex items-center gap-2 min-w-0">
                                            <div className="w-9 h-9 rounded-full bg-[#4ABDBD] flex items-center justify-center flex-shrink-0">
                                                <Building2 className="w-4 h-4 text-white" />
                                            </div>
                                            <div className="flex flex-col min-w-0">
                                                <span className="font-semibold text-sm text-gray-700 truncate">{tenant.name || t('moc.hr_detail_hospital', 'Hospital')}</span>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-1.5 flex-shrink-0">
                                            <button
                                                data-testid={`view-tenant-mobile-${tenant.tenantId}`}
                                                onClick={() => navigate(`/tenants/${tenant.tenantId}/view`)}
                                                title={t('tenantpage.view', 'View')}
                                                className="text-[#4ABDBD] p-2 rounded-lg bg-blue-50 cursor-pointer"
                                            >
                                                <Eye className="h-4 w-4" />
                                            </button>
                                            <button
                                                data-testid={`plan-tenant-mobile-${tenant.tenantId}`}
                                                onClick={() => navigate(`/tenants/${tenant.tenantId}/plan`)}
                                                title={t('tenantBilling.plan_action', 'Plan')}
                                                className="text-[#4ABDBD] p-2 rounded-lg bg-blue-50 cursor-pointer"
                                            >
                                                <BadgeIndianRupee className="h-4 w-4" />
                                            </button>
                                            {canManage && (
                                            <button
                                                data-testid={`edit-tenant-mobile-${tenant.tenantId}`}
                                                onClick={() => navigate(`/tenants/${tenant.tenantId}/edit`, { state: { tenant } })}
                                                className="text-[#4ABDBD] p-2 rounded-lg bg-blue-50 cursor-pointer"
                                            >
                                                <Pencil className="h-4 w-4" />
                                            </button>
                                            )}
                                        </div>
                                    </div>
                                    <div className="mt-2 flex items-center justify-between text-xs text-gray-600">
                                        <span className="inline-flex items-center gap-2">
                                            <Stethoscope className="w-3.5 h-3.5 text-[#4ABDBD]" />{tenant.doctorCount ?? 0}
                                            <Users className="w-3.5 h-3.5 text-[#4ABDBD] ml-1" />{tenant.adminCount ?? 0}
                                            {tenant.isOpenPlatform ? (
                                                <span className="ml-1 font-semibold text-gray-400">
                                                    {t('tenantpage.platform_tenant_short', 'Platform')}
                                                </span>
                                            ) : tenant.planPricing ? (
                                                <span className="ml-1 font-semibold text-[#2D8A8A]">
                                                    {inrFormatter.format(Number(tenant.planPricing.finalMonthly) || 0)}
                                                    <span className="font-normal text-gray-400">/{t('tenantpage.per_month_short', 'mo')}</span>
                                                </span>
                                            ) : null}
                                        </span>
                                        <button
                                            type="button"
                                            role="switch"
                                            aria-checked={isActive}
                                            onClick={() => canManage && !mutating && handleStatusToggle(tenant)}
                                            disabled={mutating || !canManage}
                                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${isActive ? 'bg-gradient-to-r from-[#4ABDBD] to-[#2D8A8A]' : 'bg-gray-300'} ${mutating ? 'opacity-50' : 'cursor-pointer'}`}
                                        >
                                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${isActive ? 'translate-x-6' : 'translate-x-1'}`} />
                                        </button>
                                    </div>
                                </div>
                            );
                        }) : (
                            <div className="text-center text-gray-500 py-6">{t('tenantpage.no_tenants_found', 'No tenants found.')}</div>
                        )}
                    </div>

                    {tenantTotal > 0 && (
                        <Pagination
                            currentPage={currentPage}
                            totalItems={tenantTotal}
                            itemsPerPage={itemsPerPage}
                            onPageChange={handlePageChange}
                        />
                    )}
                </>
            )}

        </div>
    );
}
